interface CookiePreference {
	cookieGroupId: string;
	enabled: boolean;
	name?: string;
}

interface DataInterface {
	CFAID: string;
	Authenticated: boolean;
	Bearer: string;
}

const rejectedPreferences: CookiePreference[] = [
	{ cookieGroupId: "1", enabled: true, name: "essential" },
	{ cookieGroupId: "2", enabled: false, name: "analytics" },
	{ cookieGroupId: "3", enabled: false, name: "functionality" },
	{ cookieGroupId: "4", enabled: false, name: "targeting" },
	{ cookieGroupId: "9", enabled: false, name: "optOut" }
];

const acceptAllPreferences: CookiePreference[] = [
	{ cookieGroupId: "1", enabled: true, name: "essential" },
	{ cookieGroupId: "2", enabled: true, name: "analytics" },
	{ cookieGroupId: "3", enabled: true, name: "functionality" },
	{ cookieGroupId: "4", enabled: true, name: "targeting" },
	{ cookieGroupId: "9", enabled: true, name: "optOut" }
];

function getOptanonCookie() {
	const cookies = document.cookie;
	const optanonCookie = cookies
		.split("; ")
		.find(cookie => cookie.startsWith("OptanonConsent="));

	return optanonCookie;
}

function endpointBasedOnUrl(endpoint: string) {
	const url = window.location.href;
	const is = url.includes("chick-fil-astage");

	if (is) {
		return `https://d2c.api.qa.crndev.chick-fil-a.com/cfa.frontend.preferences.v1.PreferencesService/${endpoint}`;
	} else {
		return `https://d2c.api.my.chick-fil-a.com/cfa.frontend.preferences.v1.PreferencesService/${endpoint}`;
	}
}

function bearerTokenBasedOnUrl() {
	const url = window.location.href;
	const is = url.includes("chick-fil-astage");

	if (is) {
		return `https://www.chick-fil-astage.com/gpc/jwtbearer`;
	} else {
		return `https://www.chick-fil-a.com/gpc/jwtbearer`;
	}
}

async function getBearerTokenFromGPC(): Promise<string | null> {
	const endpoint: string = `${bearerTokenBasedOnUrl()}`;

	try {
		const response: Response = await fetch(endpoint, {
			method: "GET",
			headers: {
				"Content-Type": "application/json"
			}
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data: DataInterface = await response.json();

		if (!data["Authenticated"]) {
			console.error("User not authenticated.");
			return null;
		}

		return data.Bearer;
	} catch (error) {
		console.error("Error fetching GPC bearer token:", error);
		return null;
	}
}

function setOptanonConsentCookie(value: string) {
	document.cookie = `${value}; domain=${document.domain}`;
}

function serializeToCookie(data: any[]) {
	return data
		.filter(item => item.includes("="))
		.map(item => {
			const [key, value] = item.split("=", 2);
			return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
		})
		.join("&");
}

function updateCookiePreferences(preferences: CookiePreference[]) {
	const optanonCookie = getOptanonCookie();
	const splitCookie = optanonCookie?.split("&");

	if (!splitCookie) {
		console.error("Optanon cookie not found");
		return null;
	}

	let groupsIndex = -1;
	for (let i = 0; i < splitCookie.length; i++) {
		if (splitCookie[i].startsWith("groups=")) {
			groupsIndex = i;
			break;
		}
	}

	splitCookie[groupsIndex] = `groups=${preferences
		.map(item => `${item.cookieGroupId}:${item.enabled ? 1 : 0}`)
		.join(",")}`;

	const cookieString = serializeToCookie(splitCookie);
	setOptanonConsentCookie(cookieString);
}

async function getUserPreferences() {
	const bearerToken = await getBearerTokenFromGPC();
	if (!bearerToken) {
		return null;
	}

	const endpoint = `${endpointBasedOnUrl("GetCookiePreferences")}`;

	try {
		const response = await fetch(endpoint, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${bearerToken}`,
				"Content-Type": "application/json"
			},
			body: JSON.stringify({})
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();
		if (!data) {
			throw new Error(`Unable to get cookie preferences to set`);
		}

		updateCookieCheckboxes(data.cookies);
	} catch (error) {
		console.error("Error fetching user preferences:", error);
		return null;
	}
}

function updateCookieCheckboxes(payload: CookiePreference[]) {
	payload.forEach(
		(cookieGroup: { cookieGroupId: string; enabled: boolean }) => {
			const id = cookieGroup.cookieGroupId;
			const checkbox = document.getElementById(`ot-group-id-${id}`);

			if (!checkbox) return null;

			if (cookieGroup.enabled === true) {
				if (!(checkbox as HTMLInputElement).checked)
					(checkbox as HTMLInputElement).click();
			} else {
				if ((checkbox as HTMLInputElement).checked) checkbox.click();
			}
		}
	);
}

function getOptanonConsent(): CookiePreference[] {
	const consentGroups = [
		{ id: "2", name: "analytics" },
		{ id: "3", name: "functionality" },
		{ id: "4", name: "targeting" },
		{ id: "9", name: "optOut" }
	];
	const optanonValues = [
		{ cookieGroupId: "1", enabled: true, name: "essential" },
		...consentGroups.map(({ id, name }) => ({
			cookieGroupId: id,
			enabled:
				(document.getElementById(`ot-group-id-${id}`) as HTMLInputElement)
					?.checked ?? false,
			name
		}))
	];

	return optanonValues;
}

async function upsertCookiePreferences(
	preferences: CookiePreference[]
): Promise<boolean> {
	const bearerToken: string | null = await getBearerTokenFromGPC();
	if (!bearerToken) {
		return false;
	}

	const endpoint: string = `${endpointBasedOnUrl("UpsertCookiePreferences")}`;

	try {
		const response: Response = await fetch(endpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${bearerToken}`
			},
			body: JSON.stringify({
				cookies: preferences,
				externalGPC: (navigator as any).globalPrivacyControl ? true : false
			})
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return true;
	} catch (error) {
		console.error("Error updating cookie preferences:", error);
		return false;
	}
}

async function handleCookiePreferenceChange(
	preferences: CookiePreference[]
): Promise<void> {
	const success: boolean = await upsertCookiePreferences(preferences);
	if (!success) {
		throw new Error(`Failed to update cookie preferences.`);
	}
}

function triggerPoliciesChange() {
	const currentConsent = getOptanonConsent();
	if (!currentConsent) return null;

	handleCookiePreferenceChange(currentConsent);
}

function addListeners() {
	const openOneTrustButtons = document.querySelectorAll(
		"#onetrust-pc-btn-handler, .optanon-toggle-display"
	);
	const rejectNonEssentialsButtons = document.querySelectorAll(
		"#onetrust-reject-all-handler"
	);
	const acceptAllButtons = document.querySelectorAll(
		"#onetrust-accept-btn-handler"
	);

	const handleSaveChanges = () => {
		triggerPoliciesChange();
	};

	const handleReject = () => {
		handleRejectNonEssentials();
	};

	const handleAcceptRecommendations = () => {
		handleAcceptAll();
	};

	const attachBannerListeners = () => {
		const saveChangesBtn = document.querySelector(
			".save-preference-btn-handler"
		);
		const saveRecomendationsBtn = document.querySelector(
			"#accept-recommended-btn-handler"
		);
		const rejectBtn = document.querySelector(".ot-pc-refuse-all-handler");

		saveChangesBtn?.removeEventListener("click", handleSaveChanges);
		rejectBtn?.removeEventListener("click", handleReject);
		saveRecomendationsBtn?.removeEventListener(
			"click",
			handleAcceptRecommendations
		);

		saveChangesBtn?.addEventListener("click", handleSaveChanges);
		rejectBtn?.addEventListener("click", handleReject);
		saveRecomendationsBtn?.addEventListener(
			"click",
			handleAcceptRecommendations
		);

		console.log("Banner listeners set");
	};

	openOneTrustButtons.forEach(button => {
		button.addEventListener("click", () => {
			setTimeout(attachBannerListeners, 1000);
		});
	});

	rejectNonEssentialsButtons.forEach(button => {
		button.removeEventListener("click", handleRejectNonEssentials);
		button.addEventListener("click", handleRejectNonEssentials);
	});

	acceptAllButtons.forEach(button => {
		button.removeEventListener("click", handleAcceptAll);
		button.addEventListener("click", handleAcceptAll);
	});
}

function handleRejectNonEssentials() {
	upsertCookiePreferences(rejectedPreferences);
}

function handleAcceptAll() {
	upsertCookiePreferences(acceptAllPreferences);
}

document.addEventListener("DOMContentLoaded", function () {
	getUserPreferences();
	addListeners();
});
